import { useCardReader } from "application/contexts/cardReader/CardReaderContext";
import { useCatalogStore } from "application/contexts/catalog/CatalogStore";
import { useIdle } from "application/contexts/idle/IdleContext";
import { useKiosk } from "application/contexts/kiosk/KioskContext";
import {
  OrientationType,
  ResolutionType,
  useScreenStore,
} from "application/contexts/screen/ScreenStore";
import { useUi } from "application/contexts/ui/UIContext";
import { ErrorCodeType } from "application/models/IError";
import Toolbar from "areas/_catalog/components/toolbar/Toolbar";
import { ICardCashless } from "domains/cashless/aggregates/ICardCashless";
import { Order } from "domains/order/agreggates/order/Order";
import { Payment } from "domains/order/agreggates/payment/payment";
import { CashlessRepository } from "Infrastructure/repositories/api/CashlessRepository";
import { CommandEletronicExtract } from "Infrastructure/repositories/api/ExtractRepository";
import { ProdutoTipo } from "Infrastructure/interfaces/PedidoMeep";
import { useAppInsights } from "Infrastructure/repositories/appInsights/AppInsights";
import { getOrderRepository } from "../../../Infrastructure/repositories/localStorage/getOrderRepository";
import { IEmitedNfce } from "modules/order/domain/models/INfce";
import { IOrderItem } from "modules/order/domain/models/IOrder";
import { IOrderLog } from "modules/order/domain/models/IOrderLog";
import { IPedidoPosDTO } from "modules/order/domain/models/IPedidoPosDTO";
import { IOrderProviderRef } from "modules/order/presentation/OrderProvider";
import { IPaymentResult } from "modules/payment/presentation/PaymentProvider";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { GetError } from "utils/GetError";
import { roundValue } from "utils/utils";
import { v4 } from "uuid";
import totemGif from "../../../assets/images/totem.gif";
import { PospaidExtract } from "../posPaidExtract/PospaidExtract";
import styles from "./PosPaid.module.scss";
import { useSession } from "application/contexts/session/SessionContext";
import { useTranslation } from "i18n/useTranslation";
import { CheckActiveService } from "domains/admin/services/CheckActiveService";

const cashlessRepository = CashlessRepository();
const orderRepositoryDB = getOrderRepository();
const checkActiveService = CheckActiveService();


export interface IPosPaid {
  orderProvider: React.RefObject<IOrderProviderRef>;
}

const PosPaid: FC<IPosPaid> = ({ orderProvider }) => {
  const { kiosk } = useKiosk();
  const { addLog } = useAppInsights();
  const { replace } = useHistory();
  const { getConfigurationProduct } = useCatalogStore();
  const { showLoading, hideLoading, toastFullscreen, toast } = useUi();
  const { handleActiveDefaultIdleTimer, resetCounter } = useIdle();
  const { sessaoIdPos } = useSession();
  const { resolution, orientation } = useScreenStore();
  const { t } = useTranslation();
  const {
    enableSensor,
    disableSensor,
    cardData,
    orderPad,
    isPaying,
    clearCardData,
  } = useCardReader();

  const [extract, setExtract] = useState<CommandEletronicExtract | null>(null);

  useEffect(() => {
    console.log("enableSensor PosPaid");
    enableSensor(true);

    return () => {
      disableSensor();
    };
  }, [enableSensor, disableSensor]);

  useEffect(() => {
    handleActiveDefaultIdleTimer();
  }, [handleActiveDefaultIdleTimer]);

  const addOrderLog = useCallback(
    async (
      order: IOrderLog | null,
      message: string,
      pedidoPOS?: IPedidoPosDTO,
      payment?: Payment,
      nfce?: IEmitedNfce[]
    ) => {
      try {
        if (order) {
          let orderLog = { ...order, payment, pedidoPOS, nfce };
          addLog("posPaid", { message, orderLog });
          await orderRepositoryDB.add(orderLog, message);
        }
      } catch (error) {
        GetError(
          error,
          "addOrderLog",
          "PosPaid",
          "Falha ao adicionar log",
          ErrorCodeType.GENERAL
        );
      }
    },
    [addLog]
  );

  const getExtract = useCallback(
    async (cardData: ICardCashless) => {
      if (kiosk) {
        try {
          showLoading();
          // Mesma rota da POS (Extract por tag): a conta é a aberta no cartão e o valor já vem com o mínimo de consumação

          const responseExtract = await cashlessRepository.getExtract(
            cardData.associationId,
            kiosk.localId,
            cardData.id,
            true
          );
          addLog("posPaid", {
            message: "Extract",
            responseExtract,
            cardData,
          });
          if (responseExtract) {
            setExtract(responseExtract);
            resetCounter();
          } else {
            setExtract(null);
          }
        } catch (error) {
          addLog("posPaid", { message: "[ERROR]Extract", error, ...cardData });
        } finally {
          hideLoading();
        }
      }
    },
    [addLog, hideLoading, kiosk, resetCounter, showLoading]
  );

  useEffect(() => {
    if (cardData) {
      getExtract(cardData);
    } else {
      setExtract(null);
    }
  }, [cardData, getExtract]);

  const { serviceFee, totalToPay } = useMemo(() => {
    let serviceFee = 0;
    let totalToPay = 0;
    if (extract) {
      const serviceFeePaid = extract.orders.reduce((total, orderItem) => {
        return (
          total +
          orderItem.items.reduce((itemTotal, item) => {
            if (item.isServiceFee && !orderItem.isPayment) {
              const _total = item.totalValue * item.quantity;
              return itemTotal + (_total ?? 0);
            }
            return itemTotal;
          }, 0)
        );
      }, 0);

      serviceFee = extract.serviceFeeConfig.serviceFeeAmount - serviceFeePaid;

      totalToPay = extract.totalToPayWithFee - serviceFee;
    }

    return {
      serviceFee,
      totalToPay,
    };
  }, [extract]);

  const validateOpenedService = useCallback(async () => {
    if (!kiosk) return;
    const localTypeId = kiosk.localTypeId;
    const localTypeId2 = kiosk.localTypeId2;
    const mustCheckActiveService = localTypeId2 === 28;
    console.log("localTypeId: ", localTypeId);
    console.log("localTypeId2: ", localTypeId2);
    console.log("mustCheckActiveService: ", mustCheckActiveService);
    const historicoAssociacaoId = extract?.accountId;
    if (!historicoAssociacaoId) {
      throw new Error("Não foi possível verificar serviço ativo. Tente novamente.");
    }
    if (mustCheckActiveService && historicoAssociacaoId) {
      try {
        showLoading();
        const result = await checkActiveService.checkActiveService(
          kiosk.localId,
          historicoAssociacaoId
        );
        if (result.active) {
          const serviceName = result.service_name ?? "Pista";
          toastFullscreen(
            `Não é possível fechar no momento pois existe uma ${serviceName} aberta.`,
            "warning"
          );
          throw new Error(`Não é possível fechar no momento pois existe uma ${serviceName} aberta.`);
        }
      } catch (error) {
        addLog && addLog("checkActiveService", { error });
        toast(
          (error as Error)?.message ?? "Falha ao verificar serviço ativo",
          "error"
        );
        throw error;
      } finally {
        hideLoading();
      }
    } else if (mustCheckActiveService && !sessaoIdPos) {
      toast(
        "Não foi possível verificar serviço ativo. Tente novamente.",
        "error"
      );
      throw new Error("Não foi possível verificar serviço ativo. Tente novamente.");
    }

  }, [addLog, hideLoading, kiosk, sessaoIdPos, showLoading, toast, toastFullscreen]);


  const validateAccount = useCallback(async () => {
    if (extract && kiosk) {
      try {
        showLoading();
        addLog("posPaid", {
          message: "Account",
          extract,
          cardData,
        });
        const result = await cashlessRepository.validateAccount({
          accountId: extract.accountId,
          amount: extract.totalToPayWithoutFee,
          equipamentId: kiosk.id,
          equipmentName: kiosk?.kioskName,
          localId: kiosk.localId,
          operatorId: kiosk.id,
          operatorName: "Totem",
          serviceFeeEnabled: !!serviceFee,
          variableServiceFee: serviceFee,
        });
        return result.isValid;
      } catch (error) {
        addLog("posPaid", {
          message: "[ERROR]Extract",
          extract,
          cardData,
          error,
        });
        // Mostra o motivo devolvido pela API (ex.: valor em aberto inválido) em vez de não responder
        toast(
          (error as any)?.response?.data?.message ??
            (error as any)?.response?.data?.Message ??
            t("cashless.errors.order_validation_failed"),
          "error"
        );
      } finally {
        hideLoading();
      }
    }
  }, [extract, kiosk, showLoading, addLog, cardData, serviceFee, hideLoading, toast, t]);

  const createPaymentOrder = useCallback(
    (_extract: CommandEletronicExtract) => {
      const newOrderId = v4();
      let newOrder: Order = {
        id: newOrderId,
        cartDate: new Date().toISOString(),
        sessionId: sessaoIdPos,
        orderItems: [
          {
            id: v4(),
            name:
              getConfigurationProduct(ProdutoTipo.Recarga)?.name ??
              "Pagamento de comanda",
            category: "",
            price: roundValue(Math.abs(totalToPay) + serviceFee),
            realPrice: roundValue(Math.abs(totalToPay) + serviceFee),
            totalPrice: roundValue(Math.abs(totalToPay) + serviceFee),
            productPrice: roundValue(Math.abs(totalToPay) + serviceFee),
            productId: getConfigurationProduct(ProdutoTipo.Recarga)?.id ?? "",
            productType: ProdutoTipo.Recarga,
            quantity: 1,
            imageUrl: "",
            thumbnailUrl: "",
            hideCatalog: false,
            description: null,
            barcode: null,
            orderComposition: null,
            compositions: null,
            printerName: null,
            translations: [],
          },
        ],
        totalValue: roundValue(Math.abs(totalToPay) + serviceFee),
        friendlyId:
          kiosk?.kioskName.replace(/\D/gm, "") +
          " " +
          newOrderId.substring(0, 4),
        createdAt: new Date().toLocaleString(),
        customer: {
          name: _extract.user?.name,
          associationId: _extract.accountId,
          orderId: newOrderId,
          tag: orderPad?.id,
          number: orderPad?.numero,
          orderPad: orderPad?.numero,
          document: _extract?.user?.document,
        },
        payer: {
          name: _extract.user?.name,
          ref: orderPad?.numero != null ? String(orderPad.numero) : undefined,
        },
      };

      if (kiosk?.serviceRatePercent) {
        const tx: IOrderItem = {
          id: v4(),
          name:
            getConfigurationProduct(ProdutoTipo.TaxaServico)?.name ??
            "Taxa de serviço",
          category: "",
          price: _extract.serviceFeeConfig.serviceFeeAmount,
          realPrice: _extract.serviceFeeConfig.serviceFeeAmount,
          totalPrice: _extract.serviceFeeConfig.serviceFeeAmount,
          description: null,
          productPrice: _extract.serviceFeeConfig.serviceFeeAmount,
          productId: getConfigurationProduct(ProdutoTipo.TaxaServico)?.id ?? "",
          productType: ProdutoTipo.TaxaServico,
          quantity: 1,
          imageUrl: "",
          thumbnailUrl: "",
          hideCatalog: false,
          barcode: null,
          orderComposition: null,
          compositions: null,
          printerName: null,
          translations: [],
        };
        newOrder.orderItems.push(tx);
      }
      return newOrder;
    },
    [getConfigurationProduct, kiosk, orderPad, serviceFee, sessaoIdPos, totalToPay]
  );

  const payAccount = useCallback(async () => {
    if (!extract) {
      addLog("posPaid", {
        message: "[ERROR]PayAccount: Falha ao obter extrato",
        cardData,
      });
      return GetError(
        new Error(t("cashless.errors.history_failed")),
        "payAccount",
        "PosPaid",
        undefined,
        ErrorCodeType.PAYMENT
      );
    }

    if (!kiosk) {
      addLog("posPaid", {
        message: "[ERROR]PayAccount: Falha ao obter configuração",
        cardData,
      });
      return GetError(
        new Error(t("cashless.errors.config_failed")),
        "payAccount",
        "PosPaid",
        undefined,
        ErrorCodeType.PAYMENT
      );
    }

    let isValid: boolean | undefined = false;

    try {
      await validateOpenedService();
      isValid = await validateAccount();
      addLog("posPaid", { message: "Comanda encontrada", extract, cardData });
    } catch (error) {
      addLog("posPaid", {
        message: "[ERROR]PayAccount: Falha ao validar comanda",
        extract,
      });
      return GetError(
        new Error(t("cashless.errors.order_validation_failed")),
        "payAccount",
        "PosPaid",
        undefined,
        ErrorCodeType.PAYMENT
      );
    }

    if (isValid) {
      isPaying.current = true;
      const paymentOrder = createPaymentOrder(extract);

      let paymentResult: IPaymentResult | undefined = undefined;

      await addOrderLog(paymentOrder, "Início do pagamento da comanda");

      try {
        paymentResult = await orderProvider.current?.startPayment(
          paymentOrder,
          "Payment"
        );

        if (paymentResult) {
          isPaying.current = false;
          clearCardData();
          await addOrderLog(
            paymentOrder,
            "Comanda paga com sucesso",
            paymentResult?.pedidoPos,
            paymentResult?.payment
          );
          toastFullscreen(t("cashless.messages.payment_success"), "success", {
            onClose: () => {
              replace("/");
            },
          });
        } else {
          isPaying.current = false;
          await addOrderLog(paymentOrder, "Falha ao pagar comanda");
          toast(t("cashless.errors.pay_order_failed"), "error");
          GetError(
            new Error(t("cashless.errors.perform_payment_failed")),
            "onClickPagarComanda",
            "PosPaid",
            undefined,
            ErrorCodeType.PAYMENT
          );
        }
      } catch (error) {
        await addOrderLog(paymentOrder, "Falha no pagamento", undefined, {
          success: false,
          errorMessage: JSON.stringify(error),
        } as Payment);
        return GetError(
          error,
          "payAccount",
          "PosPaid",
          undefined,
          ErrorCodeType.PAYMENT
        );
      }
    } else {
      addLog("posPaid", {
        message: "[ERROR]PayAccount: Falha ao validar comanda",
        cardData,
      });
      // Erro da API já foi exibido em validateAccount; aqui só a recusa sem erro (isValid false)
      if (isValid === false) {
        toast(t("cashless.errors.order_validation_failed"), "error");
      }
      return GetError(
        new Error("Falha ao validar comanda"),
        "payAccount",
        "PosPaid",
        undefined,
        ErrorCodeType.PAYMENT
      );
    }
  }, [
    addLog,
    addOrderLog,
    cardData,
    clearCardData,
    createPaymentOrder,
    extract,
    isPaying,
    kiosk,
    orderProvider,
    replace,
    toast,
    toastFullscreen,
    validateAccount,
    t
  ]);

  return (
    <div id={styles.PosPaid}>
      {extract ? (
        <PospaidExtract
          onClickPayment={payAccount}
          extract={extract}
          serviceRatePercent={kiosk?.serviceRatePercent}
          serviceRate={kiosk?.serviceRate ? serviceFee : null}
        />
      ) : (
        <div className={styles.readCard}>
          <Toolbar hideWelcome title={kiosk?.localName ?? ""} />
          <div className={styles.content}>
            <span
              style={{
                fontSize: resolution === ResolutionType.FULL_HD ? 45 : 30,
              }}
            >
              {t("cashless.read_card_instruction")}
            </span>
            <img
              src={totemGif}
              alt="Animação"
              style={{
                width:
                  orientation === OrientationType.LANDSCAPE
                    ? 500
                    : resolution === ResolutionType.FULL_HD
                      ? 1000
                      : 700,
                height:
                  orientation === OrientationType.LANDSCAPE
                    ? 500
                    : resolution === ResolutionType.FULL_HD
                      ? 1000
                      : 700,
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PosPaid;
